import { Action, createReducer, on } from '@ngrx/store';
import { AppProducts } from 'src/app/app.constants';
import { defaultTimeZone } from 'src/app/shared/utils/timezone';
import {
    AppSettingsDto,
    ClientCustomizationDataDto,
    CurrencyDto,
    EnterpriseUserDataDto,
    ListItemCategoryDto,
    ListVersionedProductDto,
    LocaleDto,
    PdfPaperSize,
    SchedulerConfig,
    UserAppFeaturesDto,
    UserRole,
    UserStateDto
} from '../../services/api.services';
import { isNullOrUndefined, sortArray } from '../../shared/utils';

import { AppUser, TrgUserClaims } from 'src/app/shared/models/app-user';
import * as AppActions from './app.actions';
import { logger } from '../../pbir/utils/logger';

export type AppCurrency = CurrencyDto & {
  displayName?: string;
};

const LOGGER = logger('AppCurrencies');

export class AppCurrencies {
  static readonly fallback = new AppCurrencies('USD', [{
    currencyName: 'USD',
    currencyCode: 'USD',
    symbol: '$'
  }]);

  readonly currencies: AppCurrency[] = [];

  constructor(
    public readonly baseCurrencyCode: string,
    currencyDtos: CurrencyDto[]) {
    let currencies = <AppCurrency[]>currencyDtos.map(c => ({
      ...c,
      displayName: `${c.currencyCode} - ${c.currencyName}`
    }));

    if (!currencies.length) {
      currencies = [...AppCurrencies.fallback.currencies];
    }

    currencies.sort((a, b) => a.currencyCode.localeCompare(b.currencyCode));

    const baseCurrencyIndex = currencies.findIndex(c => c.currencyCode === baseCurrencyCode);
    if (baseCurrencyIndex != 0) {
      if (baseCurrencyIndex == -1) {
        LOGGER.error('Base currency not found in currency list');
        this.baseCurrencyCode = currencies.length ? currencies[0].currencyCode : this.fallbackCurrency.currencyCode;
      }
      else {
        const baseCurrency = currencies[baseCurrencyIndex];

        const targetCurrency = {
          currencyName: 'Target',
          currencyCode: 'Target',
          displayName: `Target Currency (${baseCurrency.currencyCode})`,
        };

        currencies = [targetCurrency, ...currencies];
      }
    }

    this.currencies = currencies;
    this.baseCurrency = this.get(this.baseCurrencyCode);
    this.targetCurrency = currencies[0];
  }

  public readonly baseCurrency: AppCurrency;
  public readonly targetCurrency: AppCurrency;

  get length() {
    return this.currencies.length;
  }

  public get(code: string, options?: { returnTarget?: boolean; }) {
    code ??= 'target';

    if (code) {
      code = code.toLowerCase();
      if (code == 'target')
        return options?.returnTarget ? this.targetCurrency : this.baseCurrency;

      let currency = this.currencies.find(c => c.currencyCode.toLowerCase() == code);
      if (currency)
        return currency;
    }

    return this.baseCurrency ?? this.fallbackCurrency;
  }

  getCurrencyCode(code: string) {
    return this.get(code).currencyCode;
  }

  getEffectiveCurrency(code: string) {
    return this.get(code);
  }

  public readonly fallbackCurrency: AppCurrency = {
    currencyCode: '???',
    currencyName: 'Unknown',
    displayName: 'Unknown'
  };
}

export interface State {
  pageComponent: string;
  pageComponentId: number;

  user: AppUser;
  userClaims: TrgUserClaims;
  userLoaded: boolean;
  userLoadFailed: boolean;

  enterpriseUserData: EnterpriseUserDataDto;

  userState: UserStateDto;
  savingUserState: boolean;

  features: UserAppFeaturesDto;

  settings: AppSettingsDto;

  appProducts: AppProducts;
  products: ListVersionedProductDto[];
  itemCategories: ListItemCategoryDto[];

  schedulerConfig: SchedulerConfig;

  clientCustomization: ClientCustomizationDataDto;

  loaded: boolean;

  locales: LocaleDto[];
}

export const initialState: State = {
  pageComponent: null,
  pageComponentId: 1,

  user: null,
  userClaims: null,
  userLoaded: false,
  userLoadFailed: false,

  enterpriseUserData: null,

  userState: {
    firstHomeDashboardSelected: null,
    lastViewedDashboardId: null,
    defaultPdfPaperSize: PdfPaperSize.Default
  },
  savingUserState: false,
  
  features: {},
  
  settings: {
    currency: 'USD',
    locale: 'en-US',
    timeZoneIana: defaultTimeZone.id,
    notificationSettings: null
  },

  products: [],
  itemCategories: [],
  appProducts: {},

  schedulerConfig: { },

  clientCustomization: null,

  locales: [],

  loaded: false
}

const appReducer = createReducer(
  initialState,


  on(AppActions.loadError, (state, data) => ({ ...state, loaded: true })),
  on(AppActions.loadSuccess, (state, {
    user,
    userClaims,
    userProfileClaims,

    enterpriseUserData,
    userState,
    
    settings,
    features,

    products,
    itemCategories,
    
    schedulerConfig,

    walkMeParameters,
    externalScripts,

    clientCustomization,
    locales
  }) => {
    const claims = {
      ...unrollClaims(userClaims),
      ...unrollClaims(userProfileClaims)
    };

    return {
      ...state,
      
      user: <AppUser>{
        ...user,
        roles: {
          isSupport: user.role >= UserRole.ClientAdmin,
          isAdmin: user.role >= UserRole.InsightsAdmin,
          isDev: user.role >= UserRole.Developer,
        },
        claims
      },
      userClaims: claims,
      userState: userState,

      loaded: true,

      products: sortArray(products, (a, b) => a.name.localeCompare(b.name)),
      appProducts: {
        list: products,
        Spend: products.find(p => p.name.toLowerCase() == 'spend')?.version.name,
        Monitor: products.find(p => p.name.toLowerCase() == 'monitor')?.version.name,
        Requests: products.find(p => p.name.toLowerCase() == 'requests')?.version.name,
        Combined: products.find(p => p.name.toLowerCase() == 'combined')?.version.name,
        DevTest: products.find(p => p.name.toLowerCase() == 'devtest')?.version.name,
        InsightsMetadata: products.find(p => p.name.toLowerCase() == 'insightsmetadata')?.version.name,
      },
      itemCategories: sortArray(itemCategories, (a, b) => a.name.localeCompare(b.name)),

      features,
      settings,

      enterpriseUserData,
      clientCustomization,
      schedulerConfig,
      locales
    };

    function unrollClaims(claims: any) {
      if (!claims)
        return null;

      const result = {};

      for (const key of Object.keys(claims)) {
        let value = claims[key];

        if (!isNullOrUndefined(value)) {
          if (typeof value !== 'string' && Array.isArray(value))
            value = value.join(' ');
        }

        result[key] = value;
      }

      return result;
    };
  }),

  on(AppActions.loadSettingsSuccess,
    (state, settings) => ({ ...state, settings })),
  on(AppActions.loadClientCustomizationSuccess,
    (state, clientCustomization) => ({ ...state, clientCustomization })),


  on(AppActions.saveUserState, (state, { userState }) =>
    ({ ...state, savingUserState: true })),
  on(AppActions.saveUserStateSuccess, (state, { userState }) =>
    ({ ...state, userState, savingUserState: false })),
  on(AppActions.saveUserStateFailure, (state) =>
    ({ ...state, savingUserState: false })),


  on(AppActions.setPageComponent, (state, { component }) => {
    return {
      ...state,
      pageComponent: component instanceof Function ? component.constructor.name : component,
      pageComponentId: state.pageComponentId + 1
    };
  }),
);

export function reducer(state: State | undefined, action: Action) {
  return appReducer(state, action);
}
